import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { ShoppingBag, User, MapPin, CreditCard, Check, ArrowLeft, Lock, Minus, Plus, Landmark } from 'lucide-react';
import { FlagEN, FlagNL } from '../components/ui/Flag';
import axios from 'axios';
import { toast } from 'sonner';
import { FlowBackground } from '../components/home/FlowBackground';
import { PAGE_BG } from '../components/home/glass';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// GA4 property is chosen by hostname in public/index.html.
const gaId = () => (typeof window !== 'undefined' && window.SLEEPSTACK_GA_ID) || null;

function trackGA4(eventName, params) {
    const id = gaId();
    if (!id || typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, { ...params, send_to: id });
}

// GA4 value/item price exclude VAT; shipping is always free.
const ga4Ecommerce = (grossTotal, quantity) => {
    const value = +(grossTotal / (1 + VAT_RATE)).toFixed(2);
    return {
        currency: 'EUR',
        value,
        tax: +(grossTotal - value).toFixed(2),
        shipping: 0,
        items: [{
            item_id: 'sleepstack_sleep_powder',
            item_name: 'Sleepstack Sleep Powder, Pre-order',
            price: +(value / quantity).toFixed(2),
            quantity,
        }],
    };
};

const readConsent = () => {
    try { return JSON.parse(localStorage.getItem('sleepstack_cookie_consent') || 'null') || {}; }
    catch { return {}; }
};

const gtagGet = (field) => new Promise((resolve) => {
    const id = gaId();
    if (!id || typeof window.gtag !== 'function') return resolve(null);
    const timer = setTimeout(() => resolve(null), 800);
    window.gtag('get', id, field, (v) => { clearTimeout(timer); resolve(v ?? null); });
});

// Only with analytics consent: pass GA client/session ids for purchase attribution.
async function collectGaContext() {
    const consent = readConsent();
    if (!consent.analytics) return { analytics_consent: false };
    const [clientId, sessionId] = await Promise.all([gtagGet('client_id'), gtagGet('session_id')]);
    if (!clientId) return { analytics_consent: true, ad_consent: !!consent.marketing };
    return {
        analytics_consent: true,
        ad_consent: !!consent.marketing,
        client_id: String(clientId),
        session_id: sessionId ? String(sessionId) : null,
    };
}

const STEPS = ['cart', 'details', 'shipping', 'payment'];

// Price constants, unit prices in EUR. Must stay in sync with backend core.py (PRE_SALE_PRICE).
const UNIT_PRICE = 35;
const REGULAR_UNIT_PRICE = 45;
// NL BTW laag tarief (voedingssupplement) — must match backend core.py VAT_RATE.
// All on-site prices are VAT-inclusive; GA4 value/item price must exclude it.
const VAT_RATE = 0.09;
const MAX_QTY = 5;
const MIN_QTY = 1;

// Format a whole-euro or decimal price for display (EUR, comma/dot per locale later if needed).
const fmt = (n) => `€${n.toFixed(2).replace(/\.00$/, '')}`;

export default function Checkout() {
    const { t, lang } = useLanguage();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailLang, setEmailLang] = useState(lang);
    const [quantity, setQuantity] = useState(1);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        street: '',
        house_number: '',
        postal_code: '',
        city: '',
        country: 'NL',
    });
    const [prefilled, setPrefilled] = useState(false);
    const beginCheckoutSent = useRef(false);

    // Autofill from saved profile when the user is logged in
    useEffect(() => {
        if (!user) return;
        setFormData(prev => ({
            ...prev,
            email: prev.email || user.email || '',
            first_name: prev.first_name || user.first_name || '',
            last_name: prev.last_name || user.last_name || '',
            street: prev.street || user.street || '',
            house_number: prev.house_number || user.house_number || '',
            postal_code: prev.postal_code || user.postal_code || '',
            city: prev.city || user.city || '',
            country: prev.country || user.country || 'NL',
        }));
        setEmailLang(user.lang || lang);
        if (user.street || user.first_name) setPrefilled(true);
    }, [user]);

    // Referral code, optional
    const [referralCode, setReferralCode] = useState('');
    const [referralState, setReferralState] = useState({ status: 'idle', message: '' }); // idle | checking | valid | invalid

    // Discount code, optional, one-time-use codes generated in the admin dashboard
    const [discountCode, setDiscountCode] = useState('');
    const [discountState, setDiscountState] = useState({ status: 'idle', message: '', amountOff: 0 }); // idle | checking | valid | invalid

    const update = (field, val) => setFormData(p => ({ ...p, [field]: val }));

    const checkReferralCode = async () => {
        const code = referralCode.trim().toUpperCase();
        if (!code) {
            setReferralState({ status: 'idle', message: '' });
            return;
        }
        setReferralState({ status: 'checking', message: '' });
        try {
            const res = await axios.post(`${API}/referral/validate`, { code });
            if (res.data.valid) {
                setReferralState({ status: 'valid', message: t('checkout.referral_valid') });
            } else {
                setReferralState({ status: 'invalid', message: t('checkout.referral_invalid') });
            }
        } catch {
            setReferralState({ status: 'invalid', message: t('checkout.referral_invalid') });
        }
    };

    const checkDiscountCode = async () => {
        const code = discountCode.trim().toUpperCase();
        if (!code) {
            setDiscountState({ status: 'idle', message: '', amountOff: 0 });
            return;
        }
        setDiscountState({ status: 'checking', message: '', amountOff: 0 });
        try {
            const res = await axios.post(`${API}/checkout/discount/validate`, { code });
            if (res.data.valid) {
                setDiscountState({
                    status: 'valid',
                    message: t('checkout.discount_valid', { amount: res.data.amount_off.toFixed(2).replace(/\.00$/, '') }),
                    amountOff: res.data.amount_off,
                });
            } else {
                setDiscountState({ status: 'invalid', message: t('checkout.discount_invalid'), amountOff: 0 });
            }
        } catch {
            setDiscountState({ status: 'invalid', message: t('checkout.discount_invalid'), amountOff: 0 });
        }
    };

    const stepIcons = [ShoppingBag, User, MapPin, CreditCard];
    const stepLabels = [t('checkout.step_cart'), t('checkout.step_details'), t('checkout.step_shipping'), t('checkout.step_payment')];

    const handlePay = async () => {
        setLoading(true);
        setEmailError('');
        try {
            const body = {
                origin_url: window.location.origin,
                email: formData.email,
                lang: emailLang,
                quantity,
                shipping_address: {
                    street: formData.street,
                    house_number: formData.house_number,
                    postal_code: formData.postal_code,
                    city: formData.city,
                    country: formData.country,
                },
            };
            if (referralCode.trim() && referralState.status === 'valid') {
                body.referral_code = referralCode.trim().toUpperCase();
            }
            if (discountCode.trim() && discountState.status === 'valid') {
                body.discount_code = discountCode.trim().toUpperCase();
            }
            body.ga = await collectGaContext();
            const res = await axios.post(`${API}/checkout/session`, body, {
                timeout: 30000,
            });
            if (!res?.data?.url) {
                throw new Error('No checkout URL returned');
            }
            window.location.href = res.data.url;
        } catch (err) {
            console.error('Checkout error', err);
            setLoading(false);
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            const isNetwork = err?.code === 'ECONNABORTED' || err?.message === 'Network Error' || !err?.response;

            // Structured 400 for a rejected customer_email → render inline next
            // to the field and jump the user back to the details step instead
            // of firing a generic payment-service toast.
            if (status === 400 && detail && typeof detail === 'object' && detail.field === 'email') {
                const msg = lang === 'nl'
                    ? 'Dit e-mailadres wordt niet geaccepteerd. Controleer het en probeer opnieuw.'
                    : detail.message || 'That email address was not accepted. Please check it and try again.';
                setEmailError(msg);
                setStep(1);
                return;
            }

            const fallback = lang === 'nl'
                ? 'Betaling opzetten mislukt. Probeer het zo opnieuw.'
                : 'We could not start the payment. Please try again in a moment.';
            let msg = fallback;
            if (typeof detail === 'string' && detail) {
                msg = detail;
            } else if (isNetwork) {
                msg = lang === 'nl'
                    ? 'Verbinding met betaalserver mislukt. Controleer je internet en probeer het opnieuw.'
                    : 'We could not reach the payment server. Check your connection and try again.';
            } else if (status && status >= 500) {
                msg = lang === 'nl'
                    ? 'De betaalservice reageerde niet goed. Probeer het over een minuutje nog eens.'
                    : 'The payment service did not respond correctly. Please try again in a minute.';
            }
            toast.error(msg, { duration: 6000 });
        }
    };

    const inputCls = "w-full h-12 px-4 border border-input rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";

    // Derived totals (pre-sale + strikethrough reference)
    const lineTotal = UNIT_PRICE * quantity;
    const regularTotal = REGULAR_UNIT_PRICE * quantity;
    const amountStr = fmt(lineTotal);
    const regularAmountStr = fmt(regularTotal);
    const discountAmount = discountState.status === 'valid' ? discountState.amountOff : 0;
    const finalTotal = Math.max(0, lineTotal - discountAmount);
    const finalAmountStr = fmt(finalTotal);

    // begin_checkout once per visit, when the cart is confirmed (checkout start).
    const startCheckout = () => {
        if (!beginCheckoutSent.current) {
            beginCheckoutSent.current = true;
            trackGA4('begin_checkout', ga4Ecommerce(lineTotal, quantity));
        }
        setStep(1);
    };

    const decQty = () => setQuantity(q => Math.max(MIN_QTY, q - 1));
    const incQty = () => setQuantity(q => Math.min(MAX_QTY, q + 1));

    return (
        <div>
            <SEO
                title="Checkout | Sleepstack"
                description="Complete your Sleepstack pre-order. Transparent evening powder with 5 functional ingredients, EUR 35 one-time, no subscription, shipping November/December 2026."
                path="/checkout"
            />
            <PreSaleBanner />
            <Navbar />
            <main className="relative isolate glass-scope min-h-screen" style={{ paddingTop: 'var(--page-offset, 128px)', background: PAGE_BG }}>
                <FlowBackground variant="order" />
                <div className="section-container py-12">
                    <div className="max-w-2xl mx-auto">
                        {/* Progress bar */}
                        <div className="flex items-center justify-between mb-10">
                            {STEPS.map((s, i) => {
                                const Icon = stepIcons[i];
                                const isComplete = i < step;
                                const isActive = i === step;
                                return (
                                    <div key={s} className="flex items-center gap-2">
                                        <div className={`flex items-center gap-2 ${i < STEPS.length - 1 ? 'flex-1' : ''}`}>
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                                isComplete ? 'bg-green-500 text-white' :
                                                isActive ? 'bg-primary text-white' :
                                                'bg-muted text-muted-foreground'
                                            }`}>
                                                {isComplete ? <Check size={15} /> : <Icon size={15} />}
                                            </div>
                                            <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-primary' : isComplete ? 'text-green-600' : 'text-muted-foreground'}`}>
                                                {stepLabels[i]}
                                            </span>
                                        </div>
                                        {i < STEPS.length - 1 && (
                                            <div className={`flex-1 h-0.5 mx-2 hidden sm:block ${i < step ? 'bg-green-400' : 'bg-border'}`} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <motion.div
                            key={step}
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Step 0: Cart */}
                            {step === 0 && (
                                <div className="card-base p-8" data-testid="checkout-cart">
                                    <h2 className="heading-sm text-foreground mb-6">{t('checkout.step_cart')}</h2>
                                    <div className="p-4 bg-accent rounded-xl mb-6">
                                        <div className="flex items-start gap-4">
                                            <div className="w-16 h-20 bg-white rounded-xl flex items-center justify-center overflow-hidden shrink-0 border border-border">
                                                <img
                                                    src="/product-pouch.webp"
                                                    alt={t('checkout.product_name')}
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="h-full w-auto object-contain"
                                                    data-testid="cart-product-image"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-foreground text-sm">{t('checkout.product_name')}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5">{t('checkout.product_desc')}</div>
                                                <div className="mt-1 text-xs text-muted-foreground flex items-baseline gap-1.5">
                                                    <span className="line-through" data-testid="unit-regular-price">{fmt(REGULAR_UNIT_PRICE)}</span>
                                                    <span className="text-foreground font-semibold" data-testid="unit-presale-price">{fmt(UNIT_PRICE)}</span>
                                                    <span>/ {lang === 'nl' ? 'stuk' : 'each'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Quantity stepper */}
                                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/60">
                                            <div>
                                                <label className="block text-xs font-medium text-foreground">{t('checkout.qty_label')}</label>
                                                <p className="text-[11px] text-muted-foreground mt-0.5">{t('checkout.qty_max_note')}</p>
                                            </div>
                                            <div className="flex items-center gap-1 bg-white border border-border rounded-full p-1" data-testid="qty-stepper">
                                                <button
                                                    type="button"
                                                    onClick={decQty}
                                                    disabled={quantity <= MIN_QTY}
                                                    aria-label={lang === 'nl' ? 'Aantal verlagen' : 'Decrease quantity'}
                                                    className="w-8 h-8 rounded-full flex items-center justify-center text-foreground hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                    data-testid="qty-decrement"
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span className="w-8 text-center font-semibold text-sm tabular-nums" data-testid="qty-value">{quantity}</span>
                                                <button
                                                    type="button"
                                                    onClick={incQty}
                                                    disabled={quantity >= MAX_QTY}
                                                    aria-label={lang === 'nl' ? 'Aantal verhogen' : 'Increase quantity'}
                                                    className="w-8 h-8 rounded-full flex items-center justify-center text-foreground hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                    data-testid="qty-increment"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Gentle volume hint, no discount framing, just context */}
                                        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed" data-testid="volume-hint">
                                            {t('checkout.volume_hint', {
                                                pouch_word: lang === 'nl'
                                                    ? (quantity === 1 ? 'zakje' : 'zakjes')
                                                    : (quantity === 1 ? 'pouch' : 'pouches'),
                                                month_word: lang === 'nl'
                                                    ? (quantity === 1 ? 'maand' : 'maanden')
                                                    : (quantity === 1 ? 'month' : 'months'),
                                                qty: quantity,
                                            })}
                                        </p>
                                    </div>

                                    <div className="space-y-2 mb-6">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{t('checkout.subtotal')}</span>
                                            <span className="flex items-baseline gap-2">
                                                <span className="text-muted-foreground line-through text-xs" data-testid="cart-subtotal-regular">{regularAmountStr}</span>
                                                <span data-testid="cart-subtotal">{amountStr}</span>
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{t('checkout.shipping')}</span>
                                            <span className="text-green-600 font-medium">{t('checkout.free')}</span>
                                        </div>
                                        <div className="h-px bg-border" />
                                        <div className="flex justify-between font-bold text-base">
                                            <span>{t('checkout.total')}</span>
                                            <span className="flex items-baseline gap-2">
                                                <span className="text-muted-foreground line-through text-xs font-normal" data-testid="cart-total-regular">{regularAmountStr}</span>
                                                <span data-testid="cart-total">{amountStr}</span>
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={startCheckout}
                                        data-testid="proceed-to-details"
                                        className="btn-primary w-full"
                                    >
                                        {t('checkout.proceed_details')}
                                    </button>

                                    {/* Calm trust points */}
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-5" data-testid="checkout-trust-points">
                                        {(Array.isArray(t('checkout.trust_points')) ? t('checkout.trust_points') : []).map((p, i) => (
                                            <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`checkout-trust-point-${i}`}>
                                                <Check size={13} className="text-green-600 shrink-0" />
                                                {p}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Step 1: Details */}
                            {step === 1 && (
                                <div className="card-base p-8" data-testid="checkout-details">
                                    <h2 className="heading-sm text-foreground mb-6">{t('checkout.step_details')}</h2>
                                    {prefilled && (
                                        <div className="mb-5 text-xs bg-primary/5 text-primary font-medium border border-primary/20 rounded-lg px-3 py-2" data-testid="autofill-notice">
                                            {lang === 'nl'
                                                ? '✓ We hebben je gegevens uit je account ingevuld. Wijzig ze hieronder als dat nodig is.'
                                                : "✓ We've filled in your details from your account. Edit below if needed."}
                                        </div>
                                    )}
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">{t('checkout.first_name')}</label>
                                                <input className={inputCls} value={formData.first_name} onChange={e => update('first_name', e.target.value)} data-testid="input-first-name" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">{t('checkout.last_name')}</label>
                                                <input className={inputCls} value={formData.last_name} onChange={e => update('last_name', e.target.value)} data-testid="input-last-name" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">{t('checkout.email')}</label>
                                            <input
                                                type="email"
                                                className={`${inputCls} ${emailError ? 'border-red-500 focus:ring-red-500/30 focus:border-red-500' : ''}`}
                                                value={formData.email}
                                                onChange={e => { update('email', e.target.value); if (emailError) setEmailError(''); }}
                                                data-testid="input-email"
                                                aria-invalid={emailError ? 'true' : 'false'}
                                                aria-describedby={emailError ? 'checkout-email-error' : undefined}
                                            />
                                            {emailError && (
                                                <p
                                                    id="checkout-email-error"
                                                    className="mt-1.5 text-xs text-red-600 leading-snug"
                                                    data-testid="checkout-email-error"
                                                >
                                                    {emailError}
                                                </p>
                                            )}
                                        </div>

                                        {/* Email language preference */}
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                                {lang === 'nl' ? 'E-mailtaal' : 'Email language'}
                                            </label>
                                            <div className="grid grid-cols-2 gap-2" data-testid="checkout-lang-toggle">
                                                {[
                                                    { v: 'en', label: 'English', Flag: FlagEN },
                                                    { v: 'nl', label: 'Nederlands', Flag: FlagNL },
                                                ].map(o => (
                                                    <button
                                                        key={o.v}
                                                        type="button"
                                                        onClick={() => setEmailLang(o.v)}
                                                        data-testid={`checkout-lang-${o.v}`}
                                                        className={`h-11 rounded-xl border text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                                                            emailLang === o.v
                                                                ? 'border-primary bg-primary/5 text-primary'
                                                                : 'border-border text-muted-foreground hover:border-primary/50'
                                                        }`}
                                                    >
                                                        <o.Flag size={14} /> {o.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                                {lang === 'nl'
                                                    ? 'We sturen je orderbevestiging en alle vervolgmails in deze taal. Je kunt dit later wijzigen in je account.'
                                                    : "We'll send your order confirmation and all follow-up emails in this language. You can change it later from your account."}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                                        <button onClick={() => setStep(0)} className="btn-ghost flex items-center justify-center gap-2 text-sm whitespace-nowrap">
                                            <ArrowLeft size={14} /> {t('checkout.back')}
                                        </button>
                                        <button
                                            onClick={() => setStep(2)}
                                            disabled={!formData.first_name || !formData.email}
                                            data-testid="proceed-to-shipping"
                                            className="flex-1 btn-primary disabled:opacity-60"
                                        >
                                            {t('checkout.proceed_shipping')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Shipping */}
                            {step === 2 && (
                                <div className="card-base p-8" data-testid="checkout-shipping">
                                    <h2 className="heading-sm text-foreground mb-6">{t('checkout.step_shipping')}</h2>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-foreground mb-1.5">{t('checkout.street')}</label>
                                                <input className={inputCls} value={formData.street} onChange={e => update('street', e.target.value)} data-testid="input-street" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5 whitespace-nowrap">{t('checkout.house_number')}</label>
                                                <input className={inputCls} value={formData.house_number} onChange={e => update('house_number', e.target.value)} data-testid="input-house-number" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">{t('checkout.postal_code')}</label>
                                                <input className={inputCls} value={formData.postal_code} onChange={e => update('postal_code', e.target.value)} data-testid="input-postal" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1.5">{t('checkout.city')}</label>
                                                <input className={inputCls} value={formData.city} onChange={e => update('city', e.target.value)} data-testid="input-city" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">{t('checkout.country')}</label>
                                            <select className={inputCls} value={formData.country} onChange={e => update('country', e.target.value)} data-testid="input-country">
                                                <option value="NL">Netherlands</option>
                                                <option value="BE">Belgium</option>
                                                <option value="DE">Germany</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                                        <button onClick={() => setStep(1)} className="btn-ghost flex items-center justify-center gap-2 text-sm whitespace-nowrap">
                                            <ArrowLeft size={14} /> {t('checkout.back')}
                                        </button>
                                        <button
                                            onClick={() => setStep(3)}
                                            disabled={!formData.street || !formData.city}
                                            data-testid="proceed-to-payment"
                                            className="flex-1 btn-primary disabled:opacity-60"
                                        >
                                            {t('checkout.proceed_payment')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Payment */}
                            {step === 3 && (
                                <div className="card-base p-8" data-testid="checkout-payment">
                                    <h2 className="heading-sm text-foreground mb-6">{t('checkout.step_payment')}</h2>

                                    {/* Order summary */}
                                    <div className="bg-accent rounded-xl p-4 mb-6">
                                        <h3 className="text-sm font-semibold text-foreground mb-3">{t('checkout.order_summary')}</h3>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">
                                                    {t('checkout.product_name')}
                                                    {quantity > 1 && <span className="ml-1 text-foreground">× {quantity}</span>}
                                                </span>
                                                <span className="flex items-baseline gap-2">
                                                    <span className="text-muted-foreground line-through text-xs" data-testid="payment-line-regular">{regularAmountStr}</span>
                                                    <span data-testid="payment-line-total">{amountStr}</span>
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">{t('checkout.shipping')}</span>
                                                <span className="text-green-600 font-medium">{t('checkout.free')}</span>
                                            </div>
                                            {discountAmount > 0 && (
                                                <div className="flex justify-between text-sm" data-testid="payment-discount-row">
                                                    <span className="text-muted-foreground">{t('checkout.discount_label')}</span>
                                                    <span className="text-green-600 font-medium" data-testid="payment-discount-amount">−{fmt(discountAmount)}</span>
                                                </div>
                                            )}
                                            <div className="h-px bg-border" />
                                            <div className="flex justify-between font-bold">
                                                <span>{t('checkout.total')}</span>
                                                <span className="flex items-baseline gap-2">
                                                    <span className="text-muted-foreground line-through text-xs font-normal" data-testid="payment-total-regular">{regularAmountStr}</span>
                                                    <span data-testid="payment-total">{finalAmountStr}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Delivery info */}
                                    <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                                        <Lock size={16} className="text-primary mt-0.5 shrink-0" />
                                        <div className="text-sm text-blue-800 leading-relaxed">
                                            <p className="font-semibold mb-1">{t('checkout.charge_note', { amount: finalAmountStr.replace('€', '') })}</p>
                                            <p className="text-xs text-blue-600">{t('checkout.refund_note')}</p>
                                        </div>
                                    </div>

                                    {/* Discount code, optional */}
                                    <div className="mb-5" data-testid="discount-block">
                                        <label className="block text-sm font-medium text-foreground mb-1.5">
                                            {t('checkout.discount_label')}
                                            <span className="text-xs text-muted-foreground font-normal ml-1">
                                                ({t('checkout.discount_optional')})
                                            </span>
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                className={`${inputCls} flex-1 uppercase`}
                                                placeholder="SLEEPXXXXX"
                                                value={discountCode}
                                                onChange={e => {
                                                    setDiscountCode(e.target.value.toUpperCase());
                                                    setDiscountState({ status: 'idle', message: '', amountOff: 0 });
                                                }}
                                                onBlur={checkDiscountCode}
                                                maxLength={16}
                                                data-testid="input-discount"
                                            />
                                            <button
                                                type="button"
                                                onClick={checkDiscountCode}
                                                disabled={!discountCode.trim() || discountState.status === 'checking'}
                                                className="bg-white border border-border rounded-xl px-4 text-sm font-semibold hover:border-primary disabled:opacity-50 transition-colors"
                                                data-testid="apply-discount-btn"
                                            >
                                                {discountState.status === 'checking' ? '…' : t('checkout.discount_apply')}
                                            </button>
                                        </div>
                                        {discountState.status === 'valid' && (
                                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1" data-testid="discount-valid">
                                                <Check size={13} /> {discountState.message}
                                            </p>
                                        )}
                                        {discountState.status === 'invalid' && (
                                            <p className="text-xs text-red-600 mt-2" data-testid="discount-invalid">
                                                {discountState.message}
                                            </p>
                                        )}
                                    </div>

                                    {/* Referral code, optional */}
                                    <div className="mb-5" data-testid="referral-block">
                                        <label className="block text-sm font-medium text-foreground mb-1.5">
                                            {t('checkout.referral_label')}
                                            <span className="text-xs text-muted-foreground font-normal ml-1">
                                                ({t('checkout.referral_optional')})
                                            </span>
                                        </label>
                                        <div className="flex gap-2">
                                            <input
                                                className={`${inputCls} flex-1 uppercase`}
                                                placeholder="ABCD1234"
                                                value={referralCode}
                                                onChange={e => {
                                                    setReferralCode(e.target.value.toUpperCase());
                                                    setReferralState({ status: 'idle', message: '' });
                                                }}
                                                onBlur={checkReferralCode}
                                                maxLength={12}
                                                data-testid="input-referral"
                                            />
                                            <button
                                                type="button"
                                                onClick={checkReferralCode}
                                                disabled={!referralCode.trim() || referralState.status === 'checking'}
                                                className="bg-white border border-border rounded-xl px-4 text-sm font-semibold hover:border-primary disabled:opacity-50 transition-colors"
                                                data-testid="check-referral-btn"
                                            >
                                                {referralState.status === 'checking' ? '…' : t('checkout.referral_check')}
                                            </button>
                                        </div>
                                        {referralState.status === 'valid' && (
                                            <p className="text-xs text-green-600 mt-2 flex items-center gap-1" data-testid="referral-valid">
                                                <Check size={13} /> {referralState.message}
                                            </p>
                                        )}
                                        {referralState.status === 'invalid' && (
                                            <p className="text-xs text-red-600 mt-2" data-testid="referral-invalid">
                                                {referralState.message}
                                            </p>
                                        )}
                                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                            {t('checkout.referral_note')}
                                        </p>
                                    </div>

                                    {/* Payment methods */}
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="bg-white border border-border rounded-lg px-3 py-2 text-primary"><CreditCard size={16} /></div>
                                        <div className="bg-white border border-border rounded-lg px-3 py-2 text-primary"><Landmark size={16} /></div>
                                        <span className="text-xs text-muted-foreground">Card · iDEAL · Apple Pay · Google Pay</span>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <button onClick={() => setStep(2)} className="btn-ghost flex items-center justify-center gap-2 text-sm whitespace-nowrap">
                                            <ArrowLeft size={14} /> {t('checkout.back')}
                                        </button>
                                        <button
                                            onClick={handlePay}
                                            disabled={loading}
                                            data-testid="complete-order-btn"
                                            className="flex-1 btn-primary disabled:opacity-70"
                                        >
                                            {loading ? (
                                                <span className="flex items-center gap-2 justify-center">
                                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Processing...
                                                </span>
                                            ) : t('checkout.complete_order', { amount: finalAmountStr.replace('€', '') })}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
